import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import TopNav from '../components/TopNav';
import GradientButton from '../components/GradientButton';
import { colors, radius, tierColors } from '../theme';

const serviceCategories = [
  { id: 'wifi_networking', label: 'Wi-Fi & Networking', emoji: '📶' },
  { id: 'printer_peripherals', label: 'Printer & Peripherals', emoji: '🖨️' },
  { id: 'virus_removal', label: 'Virus Removal', emoji: '🦠' },
  { id: 'smart_home', label: 'Smart Home', emoji: '🏠' },
  { id: 'tv_device_setup', label: 'TV & Device Setup', emoji: '📺' },
  { id: 'social_media', label: 'Social Media', emoji: '📱' },
  { id: 'pc_setup', label: 'PC Setup', emoji: '💻' },
  { id: 'social_media_account_recovery', label: 'Account Recovery', emoji: '🔐' },
];

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function HomePage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const name = profile?.displayName?.split(' ')[0] || 'there';
  const isTechPass = profile?.plan === 'techpass';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.background,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        paddingBottom: 80,
      }}
    >
      <TopNav />

      <div style={{ padding: '20px 16px 0' }}>
        {/* Greeting */}
        <div style={{ marginBottom: 20 }}>
          <h1
            style={{
              color: colors.textPrimary,
              fontSize: 26,
              fontWeight: 700,
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            {getGreeting()}, {name} 👋
          </h1>
          <p style={{ color: colors.textSecondary, margin: '6px 0 0', fontSize: 14 }}>
            What can we fix for you today?
          </p>
        </div>

        {/* Tech Pass upsell (free users only) */}
        {!isTechPass && (
          <div
            onClick={() => navigate('/profile')}
            style={{
              background: 'linear-gradient(135deg, #663399, #E040A0)',
              borderRadius: 16,
              padding: '16px 18px',
              marginBottom: 20,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 4px 20px rgba(102,51,153,0.4)',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 18 }}>⚡</span>
                <span style={{ color: colors.textPrimary, fontWeight: 700, fontSize: 16 }}>
                  Upgrade to Tech Pass
                </span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.85)', margin: 0, fontSize: 12 }}>
                $19.99/mo · No service fees · Free emergency dispatch
              </p>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 22 }}>›</span>
          </div>
        )}

        {/* Tech Pass active banner */}
        {isTechPass && (
          <div
            style={{
              background: 'rgba(48,209,88,0.12)',
              borderRadius: 14,
              padding: '12px 16px',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              border: '1px solid rgba(48,209,88,0.25)',
            }}
          >
            <span style={{ fontSize: 20 }}>⚡</span>
            <div>
              <span style={{ color: colors.success, fontWeight: 700, fontSize: 14 }}>
                Tech Pass Active
              </span>
              <p style={{ color: colors.textSecondary, margin: '2px 0 0', fontSize: 12 }}>
                No service fees · Free emergency dispatch
              </p>
            </div>
          </div>
        )}

        {/* Service Categories */}
        <h2
          style={{
            color: colors.textPrimary,
            fontSize: 17,
            fontWeight: 700,
            margin: '0 0 14px',
          }}
        >
          What do you need help with?
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            marginBottom: 24,
          }}
        >
          {serviceCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => navigate(`/booking?category=${cat.id}`)}
              style={{
                background: colors.surface,
                border: '1.5px solid rgba(26,10,46,0.06)',
                borderRadius: 14,
                padding: '16px 14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 8,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'transform 0.1s, background 0.15s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(102,51,153,0.25)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = colors.surface;
              }}
            >
              <span style={{ fontSize: 28 }}>{cat.emoji}</span>
              <span
                style={{
                  color: colors.textPrimary,
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                {cat.label}
              </span>
            </button>
          ))}
        </div>

        {/* Get Help Now CTA */}
        <GradientButton
          onClick={() => navigate('/booking')}
          fullWidth
          style={{ fontSize: 17, padding: '16px 24px', borderRadius: 16 }}
        >
          ⚡ Get Help Now
        </GradientButton>

        <p
          style={{
            color: colors.textSecondary,
            fontSize: 12,
            textAlign: 'center',
            marginTop: 10,
          }}
        >
          Typically arrives in 15–30 minutes
        </p>
      </div>

      <BottomNav />
    </div>
  );
}
